import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import truemoneyLogo from '@/assets/truemoney-logo.png'
import type { TenantThemeConfig } from '../types/theme'
import DepositPageHeader from '../components/DepositPageHeader'
import * as authService from '../services/authService'
import * as walletService from '../services/walletService'
import './ModernTopupPage.css'
import {
  getDepositChannels,
  getDepositPresetAmounts,
  syncPresetAmountsAfterDepositChannelsUpdate,
  getPublicPromptPayAccounts,
  getPublicDepositAccounts,
  isTrueMoneyDepositAccount,
  mapPromptPayAccountsToDepositAccounts,
  resolveDepositAccountForAmount,
  type DepositChannels,
  type PublicDepositAccount,
} from '../services/depositAccountService'
import { getDepositFlowConfig, type DepositFlowConfig } from '../services/depositFlowService'
import { pendingDepositDraftService, type PendingDepositMethod } from '../services/pendingDepositDraftService'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernTopupPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

interface TopupTab {
  id: number
  label: string
  method: PendingDepositMethod
  kind: 'payment' | 'bank' | 'true_money' | 'prompt_pay' | 'decimal'
}

const DEFAULT_PRESET_AMOUNTS = [100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000]
const TRUEMONEY_LOGO_URL = truemoneyLogo

const DEFAULT_DEPOSIT_FLOW: DepositFlowConfig = {
  tenantId: '',
  tenantName: '',
  paymentServiceSelected: false,
  paymentGatewayEnabled: false,
  slipServiceSelected: false,
  slipVerificationEnabled: false,
  effectiveDepositMode: 'manual',
  depositAutoCreditEnabled: true,
  depositAutoCreditViaPayment: true,
  depositAutoCreditViaSlip: true,
  depositAutoCreditViaSms: true,
  automationSettingsWarning: null,
}

const createDraftId = () => `deposit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const amountButtonSurfaceStyle = (selected: boolean): React.CSSProperties => ({
  minHeight: 88,
  borderRadius: 22,
  padding: '0.9rem 0.85rem',
  border: selected ? '1px solid rgba(37, 99, 235, 0.18)' : '1px solid rgba(15, 23, 42, 0.08)',
  background: selected
    ? 'linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(241,245,249,0.95) 100%)',
  boxShadow: selected
    ? '0 18px 32px rgba(37, 99, 235, 0.28)'
    : '0 10px 24px rgba(15, 23, 42, 0.10), inset 0 1px 0 rgba(255,255,255,0.92)',
  transform: selected ? 'translateY(-2px)' : 'translateY(0)',
  transition: 'all 0.2s ease',
})

const lightPanelLabelStyle: React.CSSProperties = {
  color: '#64748b',
  fontWeight: 600,
}

const lightPanelTextStyle: React.CSSProperties = {
  color: '#0f172a',
}

const lightPanelHelperStyle: React.CSSProperties = {
  color: '#475569',
  fontWeight: 500,
}

const buildSenderAccountSnapshot = (account?: authService.UserBankAccount | null) => {
  if (!account) return undefined
  return {
    bankCode: account.bankCode || '',
    bankName: account.bankName || '',
    accountNumber: account.accountNumber || '',
    accountName: account.accountName || '',
  }
}

const ModernTopupPage: React.FC<ModernTopupPageProps> = ({ themeConfig }) => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(0)
  const [transferAmount, setTransferAmount] = useState('')
  const [decimalAmount, setDecimalAmount] = useState('')
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [userSummary, setUserSummary] = useState<authService.UserInfoSummary | null>(null)
  const [userBankAccounts, setUserBankAccounts] = useState<authService.UserBankAccount[]>([])
  const [depositAccounts, setDepositAccounts] = useState<PublicDepositAccount[]>([])
  const [promptPayAccounts, setPromptPayAccounts] = useState<PublicDepositAccount[]>([])
  const [depositFlow, setDepositFlow] = useState<DepositFlowConfig>(DEFAULT_DEPOSIT_FLOW)
  const [bankCheckDone, setBankCheckDone] = useState(false)
  const [depositChannels, setDepositChannels] = useState<DepositChannels>({
    bank: true,
    trueMoney: true,
    promptPay: true,
    decimalReservation: true,
    allowSubmitWithoutSlip: true,
  })
  const [presetAmounts, setPresetAmounts] = useState<number[]>(DEFAULT_PRESET_AMOUNTS)

  const applyDepositChannels = (channels?: Partial<DepositChannels> | null) => {
    if (!channels || typeof channels !== 'object') return false
    setDepositChannels({
      bank: channels.bank !== false,
      trueMoney: channels.trueMoney !== false,
      promptPay: channels.promptPay !== false,
      decimalReservation: channels.decimalReservation !== false,
      allowSubmitWithoutSlip: channels.allowSubmitWithoutSlip !== false,
    })
    return true
  }

  useEffect(() => {
    if (!authService.isAuthenticated()) return
    Promise.all([
      walletService.getWallet(),
      authService.fetchUserInfoSummary(),
      authService.fetchUserBankAccounts(),
    ]).then(([walletRes, summary, bankAccounts]) => {
      if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
      else setWalletData(null)
      setUserSummary(summary ?? null)
      const nextBankAccounts = Array.isArray(bankAccounts) ? bankAccounts : []
      setUserBankAccounts(nextBankAccounts)
      setBankCheckDone(true)
      if (nextBankAccounts.length === 0) navigate('/member', { replace: true })
    })
  }, [navigate])

  useEffect(() => {
    const tenantId = themeConfig.tenantId
    if (!tenantId) return

    Promise.all([
      getPublicDepositAccounts(tenantId),
      getPublicPromptPayAccounts(tenantId),
      getDepositChannels(tenantId),
      getDepositPresetAmounts(tenantId),
      getDepositFlowConfig(tenantId),
    ]).then(([accounts, promptPayList, channels, presetAmountsResponse, flow]) => {
      setDepositAccounts(accounts)
      setPromptPayAccounts(mapPromptPayAccountsToDepositAccounts(promptPayList))
      setDepositChannels(channels)
      setPresetAmounts(presetAmountsResponse)
      setDepositFlow(flow)
    })
  }, [themeConfig.tenantId])

  useEffect(() => {
    const tenantId = themeConfig.tenantId
    if (!tenantId) return

    const onDepositChannelsUpdated = (
      event: CustomEvent<{
        tenantId?: string
        content?: {
          depositChannels?: Partial<DepositChannels>
          settings?: { depositChannels?: Partial<DepositChannels> }
        }
      }>
    ) => {
      if (event.detail?.tenantId !== tenantId) return
      const contentChannels = event.detail?.content?.depositChannels || event.detail?.content?.settings?.depositChannels
      if (!applyDepositChannels(contentChannels)) {
        getDepositChannels(tenantId, { forceRefresh: true }).then(setDepositChannels)
      }
      void syncPresetAmountsAfterDepositChannelsUpdate(tenantId, contentChannels).then(setPresetAmounts)
      getDepositFlowConfig(tenantId).then(setDepositFlow)
    }

    const onDepositAccountsUpdated = (event: CustomEvent<{ tenantId?: string }>) => {
      if (event.detail?.tenantId !== tenantId) return
      Promise.all([
        getPublicDepositAccounts(tenantId),
        getPublicPromptPayAccounts(tenantId),
      ]).then(([accounts, promptPayList]) => {
        setDepositAccounts(accounts)
        setPromptPayAccounts(mapPromptPayAccountsToDepositAccounts(promptPayList))
      })
    }

    window.addEventListener('deposit_channels_updated', onDepositChannelsUpdated as EventListener)
    window.addEventListener('deposit_accounts_updated', onDepositAccountsUpdated as EventListener)
    return () => {
      window.removeEventListener('deposit_channels_updated', onDepositChannelsUpdated as EventListener)
      window.removeEventListener('deposit_accounts_updated', onDepositAccountsUpdated as EventListener)
    }
  }, [themeConfig.tenantId])

  const defaultBankAccount = useMemo(
    () =>
      depositAccounts.find((account) => !isTrueMoneyDepositAccount(account) && account.isDefault) ??
      depositAccounts.find((account) => !isTrueMoneyDepositAccount(account)) ??
      null,
    [depositAccounts]
  )
  const enteredTransferAmount = useMemo(() => {
    const amount = parseFloat(transferAmount.trim())
    return Number.isFinite(amount) ? amount : NaN
  }, [transferAmount])
  const selectedBankAccount = useMemo(
    () => resolveDepositAccountForAmount(depositAccounts, enteredTransferAmount, { channel: 'bank' }) ?? defaultBankAccount,
    [defaultBankAccount, depositAccounts, enteredTransferAmount]
  )
  const selectedTrueMoneyAccount = useMemo(
    () => resolveDepositAccountForAmount(depositAccounts, enteredTransferAmount, { channel: 'true_money' }),
    [depositAccounts, enteredTransferAmount]
  )
  const selectedPromptPayAccount = useMemo(
    () => resolveDepositAccountForAmount(promptPayAccounts, enteredTransferAmount, { channel: 'promptpay' }),
    [enteredTransferAmount, promptPayAccounts]
  )
  const enteredDecimalAmount = useMemo(() => {
    const amount = parseFloat(decimalAmount.trim())
    return Number.isFinite(amount) ? amount : NaN
  }, [decimalAmount])
  const selectedDecimalReceiverAccount = useMemo(
    () => resolveDepositAccountForAmount(depositAccounts, enteredDecimalAmount, { channel: 'bank' }) ?? defaultBankAccount,
    [defaultBankAccount, depositAccounts, enteredDecimalAmount]
  )
  const selectedUserBankAccount = userBankAccounts[0] ?? null

  const availableTabs = useMemo<TopupTab[]>(() => {
    const tabs: TopupTab[] = []
    if (depositChannels.bank) {
      tabs.push({
        id: 0,
        label: 'ธนาคาร',
        method: 'bank_transfer',
        kind: 'bank',
      })
    }
    if (depositChannels.trueMoney && depositAccounts.some((account) => isTrueMoneyDepositAccount(account))) {
      tabs.push({
        id: 1,
        label: 'TrueMoney',
        method: 'true_money',
        kind: 'true_money',
      })
    }
    if (depositChannels.promptPay && promptPayAccounts.length > 0) {
      tabs.push({
        id: 2,
        label: 'PromptPay',
        method: 'prompt_pay',
        kind: 'prompt_pay',
      })
    }
    if (depositChannels.decimalReservation) {
      tabs.push({
        id: 3,
        label: 'ฝากทศนิยม',
        method: 'decimal_reservation',
        kind: 'decimal',
      })
    }
    return tabs
  }, [depositAccounts, depositChannels, promptPayAccounts.length])

  useEffect(() => {
    if (availableTabs.some((tab) => tab.id === activeTab)) return
    if (availableTabs[0]) setActiveTab(availableTabs[0].id)
  }, [activeTab, availableTabs])

  const activeTabConfig = useMemo(
    () => availableTabs.find((tab) => tab.id === activeTab) ?? availableTabs[0] ?? null,
    [activeTab, availableTabs]
  )
  const activeMethod = activeTabConfig?.method ?? 'bank_transfer'
  const methodColClass =
    availableTabs.length <= 1
      ? 'col-12'
      : availableTabs.length === 2
        ? 'col-12 col-md-6'
        : availableTabs.length === 3
          ? 'col-12 col-md-4'
          : 'col-12 col-md-6 col-lg-3'
  const selectedAccount = useMemo(() => {
    if (activeMethod === 'bank_transfer' && depositFlow.paymentGatewayEnabled) return null
    if (activeMethod === 'true_money') return selectedTrueMoneyAccount
    if (activeMethod === 'prompt_pay') return selectedPromptPayAccount
    if (activeMethod === 'bank_transfer') return selectedBankAccount ?? defaultBankAccount
    return null
  }, [activeMethod, defaultBankAccount, depositFlow.paymentGatewayEnabled, selectedBankAccount, selectedPromptPayAccount, selectedTrueMoneyAccount])
  const senderAccountSnapshot = useMemo(
    () => buildSenderAccountSnapshot(selectedUserBankAccount),
    [selectedUserBankAccount]
  )

  const balanceText =
    walletData != null
      ? `${walletService.formatCurrency(walletService.getDisplayBalance(walletData))} ฿`
      : '0.00 ฿'
  const pointsText = userSummary?.points != null ? `${Number(userSummary.points).toFixed(2)} pts` : '0.00 pts'
  const userBankSummaryText = selectedUserBankAccount
    ? `${selectedUserBankAccount.accountNumber} | ${selectedUserBankAccount.bankName}`
    : 'ยังไม่ได้ผูกบัญชี'
  const selectedPresetAmount = useMemo(
    () => presetAmounts.find((amount) => String(amount) === transferAmount.trim()) ?? null,
    [presetAmounts, transferAmount]
  )
  const selectedDecimalPresetAmount = useMemo(
    () => presetAmounts.find((amount) => String(amount) === decimalAmount.trim()) ?? null,
    [decimalAmount, presetAmounts]
  )
  const canContinue =
    activeMethod === 'decimal_reservation'
      ? Boolean(decimalAmount.trim()) && parseFloat(decimalAmount) > 0
      : Boolean(transferAmount.trim()) && parseFloat(transferAmount) > 0 && ((activeMethod === 'bank_transfer' && depositFlow.paymentGatewayEnabled) || Boolean(selectedAccount))

  const goToSummary = () => {
    const tenantId = themeConfig.tenantId
    const currentUser = authService.getCurrentUser()
    if (!tenantId || !currentUser?.id) return

    if (activeMethod === 'decimal_reservation') {
      const amount = decimalAmount.trim()
      if (!amount || !/^\d+$/.test(amount) || parseFloat(amount) <= 0) return
      navigate('/topup/reserve', {
        state: {
          amount,
          depositAccount: selectedDecimalReceiverAccount,
          senderAccount: senderAccountSnapshot,
        },
      })
      return
    }

    const amountValue = transferAmount.trim()
    if (!amountValue || !/^\d*\.?\d*$/.test(amountValue) || parseFloat(amountValue) <= 0) return

    const draftId = createDraftId()
    const nextAmount = String(Math.floor(parseFloat(amountValue)))
    const usesPaymentGateway = activeMethod === 'bank_transfer' && depositFlow.paymentGatewayEnabled
    const fallbackBankAccount =
      activeMethod === 'bank_transfer'
        ? (selectedBankAccount ?? defaultBankAccount ?? undefined)
        : undefined
    const nextDepositAccount = activeMethod === 'bank_transfer'
      ? fallbackBankAccount
      : selectedAccount ?? undefined

    pendingDepositDraftService.upsert(tenantId, currentUser.id, {
      id: draftId,
      amount: nextAmount,
      depositMethod: activeMethod,
      depositAccount: nextDepositAccount,
      senderAccount: senderAccountSnapshot,
      usesPaymentGateway,
    })

    const navigationState = {
      amount: nextAmount,
      depositAccount: nextDepositAccount,
      depositMethod: activeMethod,
      senderAccount: senderAccountSnapshot,
      usesPaymentGateway,
    }
    const shouldSkipConfirmPage = !usesPaymentGateway && activeMethod !== 'prompt_pay'

    navigate(
      shouldSkipConfirmPage
        ? `/topup/slip?draft=${encodeURIComponent(draftId)}`
        : `/topup/confirm?draft=${encodeURIComponent(draftId)}`,
      { state: navigationState }
    )
  }

  const renderTabContent = (tab: TopupTab) => {
    if (tab.kind === 'payment') {
      return (
        <span className="d-inline-flex align-items-center gap-2">
          <i className="fa fa-qrcode" />
          <span>{tab.label}</span>
        </span>
      )
    }

    if (tab.kind === 'true_money') {
      return (
        <span className="topup-method-btn__truemoney">
          <img
            src={selectedTrueMoneyAccount?.bankIcon || TRUEMONEY_LOGO_URL}
            alt=""
            aria-hidden="true"
            className="topup-method-btn__truemoney-logo"
            onError={(event) => {
              const element = event.target as HTMLImageElement
              if (element.src !== TRUEMONEY_LOGO_URL) element.src = TRUEMONEY_LOGO_URL
            }}
          />
          <span className="topup-method-btn__truemoney-text">TrueMoney</span>
        </span>
      )
    }

    if (tab.kind === 'bank') {
      return (
        <span className="topup-method-btn__label">
          <span className="topup-method-btn__icon" aria-hidden="true">
            <i className="fas fa-university" />
          </span>
          <span>{tab.label}</span>
        </span>
      )
    }

    if (tab.kind === 'decimal') {
      return (
        <span className="topup-method-btn__label">
          <span className="topup-method-btn__icon" aria-hidden="true">
            <i className="fas fa-coins" />
          </span>
          <span>{tab.label}</span>
        </span>
      )
    }

    if (tab.kind === 'prompt_pay') {
      return (
        <span className="topup-method-btn__label">
          <span className="topup-method-btn__icon" aria-hidden="true">
            <i className="fas fa-qrcode" />
          </span>
          <span>{tab.label}</span>
        </span>
      )
    }

    return <span>{tab.label}</span>
  }

  const renderAmountButton = (
    amount: number,
    selected: boolean,
    onClick: () => void,
    badgeText: string
  ) => (
    <button
      type="button"
      className="w-100 border-0 text-start position-relative overflow-hidden"
      onClick={onClick}
      style={amountButtonSurfaceStyle(selected)}
    >
      <div className="d-flex align-items-start justify-content-between gap-2">
        <span
          className={`small px-2 py-1 ${selected ? 'bg-white text-primary' : 'bg_op_secondary c_quinary'}`}
          style={{
            borderRadius: 999,
            fontWeight: 600,
            letterSpacing: '0.01em',
          }}
        >
          {selected ? 'เลือกแล้ว' : badgeText}
        </span>
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: selected ? 'rgba(255,255,255,0.95)' : 'rgba(59,130,246,0.18)',
            boxShadow: selected ? '0 0 0 4px rgba(255,255,255,0.18)' : 'none',
            marginTop: 6,
            flexShrink: 0,
          }}
        />
      </div>
      <div className={`mt-3 ${selected ? 'c_tertiary' : 'c_primary'}`} style={{ fontSize: '1.2rem', fontWeight: 800, lineHeight: 1.1 }}>
        {amount.toLocaleString()}
      </div>
      <div className={`small mt-1 ${selected ? 'c_tertiary' : 'c_quinary'}`} style={{ opacity: selected ? 0.92 : 1 }}>
        บาท
      </div>
    </button>
  )

  const depositEnabled = themeConfig.settings?.depositEnabled !== false

  if (!bankCheckDone) {
    return (
      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5 c_tertiary">กำลังโหลด...</div>
          </div>
        </div>
      </div>
    )
  }

  if (!depositEnabled) {
    return (
      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-10 offset-lg-1">
              <div className="light_sd bg_op_secondary align-items-center bdc_primary p-4 p-md-5 border_30 text-center mt-3">
                <h5 className="c_tertiary font_md mb-3">ปิดการฝากเงินชั่วคราว</h5>
                <p className="c_quinary mb-0">ขณะนี้ระบบปิดการรับฝากเงิน กรุณาติดต่อแอดมินหรือลองใหม่ภายหลัง</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="row mx__1 mt-3" style={{ rowGap: '1rem' }}>
              <DepositPageHeader
                balanceText={balanceText}
                pointsText={pointsText}
                userBankSummaryText={userBankSummaryText}
                showSummaryCard={false}
              />

              <div className="col-12 px-2">
                <div className="light_sd bg_op_secondary align-items-center bdc_primary p-3 p-md-4 border_30">
                  <div className="small c_quinary mb-2">เลือกวิธีการฝาก</div>
                  <div className="border_30 px-2 px-md-3 py-2 py-md-3 topup-method-surface">
                    <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
                      {availableTabs.map((tab) => {
                        const selected = activeTabConfig?.id === tab.id
                        return (
                          <div key={tab.id} className={`${methodColClass} px-1`}>
                            <button
                              type="button"
                              className={`topup-method-btn h-100 w-100 border-0${selected ? ' topup-method-btn--active' : ''}`}
                              onClick={() => setActiveTab(tab.id)}
                              aria-pressed={selected}
                              aria-label={tab.label}
                            >
                              <span className="topup-method-btn__content">
                                {renderTabContent(tab)}
                              </span>
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 px-2">
                {activeMethod !== 'decimal_reservation' ? (
                  <div className="light_sd bg_op_secondary align-items-center bdc_primary p-3 p-md-4 border_30">
                    <div className="row mx__1" style={{ rowGap: '1rem' }}>
                      <div className="col-12 px-2">
                        <div
                          className="border_30 px-3 py-3 h-100"
                          style={{
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(241,245,249,0.94) 100%)',
                            boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
                          }}
                        >
                          {presetAmounts.length > 0 && (
                          <>
                          <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
                            <div>
                              <div className="small" style={lightPanelLabelStyle}>เลือกยอดด่วน</div>
                              <div style={{ ...lightPanelTextStyle, fontSize: '0.95rem', fontWeight: 700 }}>
                                แตะ 1 ครั้งเพื่อเลือกยอดที่ต้องการฝาก
                              </div>
                            </div>
                            {selectedPresetAmount && (
                              <span className="small px-3 py-2 bg_primary c_tertiary border_30">
                                เลือกไว้ {selectedPresetAmount.toLocaleString()} บาท
                              </span>
                            )}
                          </div>

                          <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
                            {presetAmounts.map((amount) => (
                              <div key={`${activeMethod}-${amount}`} className="col-6 col-md-4 px-1">
                                {renderAmountButton(
                                  amount,
                                  selectedPresetAmount === amount,
                                  () => setTransferAmount(String(amount)),
                                  'ยอดด่วน'
                                )}
                              </div>
                            ))}
                          </div>
                          </>
                          )}

                          <div className="mt-3">
                            <div className="small mb-1" style={lightPanelLabelStyle}>ระบุจำนวนเงินเอง</div>
                            <input
                              type="number"
                              inputMode="decimal"
                              min="1"
                              step="0.01"
                              placeholder="กรอกจำนวนเงินที่ต้องการฝาก"
                              value={transferAmount}
                              onChange={(event) => {
                                const value = event.target.value
                                if (value === '' || /^\d*\.?\d*$/.test(value)) setTransferAmount(value)
                              }}
                              className="w-100 text-center border-0 px-3"
                              style={{
                                minHeight: 72,
                                fontSize: '1.75rem',
                                fontWeight: 700,
                                color: '#0f172a',
                                background: 'rgba(255,255,255,0.96)',
                                borderRadius: 22,
                                border: '1px solid rgba(148, 163, 184, 0.35)',
                                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.92)',
                              }}
                              aria-label="จำนวนเงินที่ต้องการฝาก"
                            />
                            <div className="small text-center mt-2" style={lightPanelHelperStyle}>
                              {presetAmounts.length > 0
                                ? 'กดเลือกยอดด้านบน หรือพิมพ์จำนวนเงินเองได้ทันที'
                                : 'พิมพ์จำนวนเงินที่ต้องการฝาก'}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 px-2">
                        <button
                          type="button"
                          className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                          disabled={!canContinue}
                          onClick={goToSummary}
                        >
                          <div className="d-flex align-items-center c_tertiary font_md">
                            <h5 className="mb-0">ยืนยันยอดและดูรายละเอียด</h5>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="light_sd bg_op_secondary align-items-center bdc_primary p-3 p-md-4 border_30">
                    <div className="row mx__1" style={{ rowGap: '1rem' }}>
                      <div className="col-12 px-2">
                        <div
                          className="align-items-center px-2 py-3 border_30 d-grid gap-3 h-100"
                          style={{
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(241,245,249,0.94) 100%)',
                            boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
                          }}
                        >
                          <div className="text-center">
                            <h6 className="mb-0" style={{ ...lightPanelTextStyle, fontWeight: 700 }}>
                              ใส่จำนวนเงินเต็มที่ต้องการเติม/ฝาก
                            </h6>
                          </div>
                          {presetAmounts.length > 0 && (
                          <>
                          <div className="d-flex justify-content-between align-items-center gap-3 px-2 flex-wrap">
                            <div>
                              <div className="small" style={lightPanelLabelStyle}>ยอดที่นิยม</div>
                              <div style={{ ...lightPanelTextStyle, fontSize: '0.95rem', fontWeight: 700 }}>
                                เลือกยอดเต็มก่อนจองทศนิยม
                              </div>
                            </div>
                            {selectedDecimalPresetAmount && (
                              <span className="small px-3 py-2 bg_primary c_tertiary border_30">
                                เลือกไว้ {selectedDecimalPresetAmount.toLocaleString()} บาท
                              </span>
                            )}
                          </div>
                          <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
                            {presetAmounts.map((amount) => (
                              <div key={`decimal-${amount}`} className="col-6 col-md-4 px-1">
                                {renderAmountButton(
                                  amount,
                                  selectedDecimalPresetAmount === amount,
                                  () => setDecimalAmount(String(amount)),
                                  'ยอดแนะนำ'
                                )}
                              </div>
                            ))}
                          </div>
                          </>
                          )}
                        </div>
                      </div>
                      <div className="col-12 px-2">
                        <div
                          className="align-items-center px-2 py-3 border_30 d-grid gap-2 overflow-hidden h-100"
                          style={{
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(241,245,249,0.94) 100%)',
                            boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
                          }}
                        >
                          <div className="text-center">
                            <h6 className="mb-2" style={lightPanelLabelStyle}>ระบุจำนวนเงินเอง</h6>
                            <h1 className="mb-0">
                              <input
                                type="number"
                                inputMode="numeric"
                                min="1"
                                step="1"
                                placeholder="กรอกจำนวนเงิน"
                                value={decimalAmount}
                                onChange={(event) => {
                                  const value = event.target.value
                                  if (value === '' || /^\d*$/.test(value)) setDecimalAmount(value)
                                }}
                                className="w-100 text-center border-0 px-2"
                                style={{
                                  minHeight: 72,
                                  fontSize: '1.75rem',
                                  fontWeight: 700,
                                  color: '#0f172a',
                                  background: 'rgba(255,255,255,0.96)',
                                  borderRadius: 22,
                                  border: '1px solid rgba(148, 163, 184, 0.35)',
                                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.92)',
                                }}
                                aria-label="จำนวนเงินฝากทศนิยม"
                              />
                            </h1>
                            <div className="small mt-2" style={lightPanelHelperStyle}>พิมพ์ยอดที่ต้องการจองได้เลย</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 px-2">
                        <button
                          type="button"
                          className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                          disabled={!canContinue}
                          onClick={goToSummary}
                        >
                          <div className="d-flex align-items-center c_tertiary font_md">
                            <h5 className="mb-0">จองการโอนเงินผ่านระบบ</h5>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernTopupPage
